import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

// Globalny kiosk-mode fullscreen (Browser Fullscreen API + body.app-fullscreen).
// Każda strona z Layout dostaje przycisk fullscreen w lewym dolnym rogu obok
// zwijania menu. Skrót Cmd/Ctrl+Shift+F. Ukrywa Chrome paski + nasze topbar
// + zwija sidebar do 56px z samą ikoną Maximize2 (wyjście z fullscreen).
// Inbox ma własną nakładkę 4-kolumnową w fs - kontekst tylko sygnalizuje
// stan, custom layout per-strona realizuje konkretny widok.

type FullscreenContextValue = {
  isFullscreen: boolean;
  toggle: () => Promise<void>;
  enter: () => Promise<void>;
  exit: () => Promise<void>;
};

const FullscreenContext = createContext<FullscreenContextValue | null>(null);

export function FullscreenProvider({ children }: { children: ReactNode }) {
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Sync state z faktycznym fullscreen stanem przeglądarki. User może wyjść
  // klawiszem ESC z poziomu Browser API - musimy złapać event i odzwierciedlić.
  useEffect(() => {
    const handler = () => {
      const active = !!document.fullscreenElement;
      setIsFullscreen(active);
      document.body.classList.toggle('app-fullscreen', active);
    };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const enter = useCallback(async () => {
    try {
      // Safari macOS wymaga userActivation - wszystkie wywołania MUSZĄ być
      // z direct user click handler. Promise odrzuca jeśli nie ma activation.
      await document.documentElement.requestFullscreen();
    } catch (err) {
      console.warn('[Fullscreen] requestFullscreen failed:', err);
    }
  }, []);

  const exit = useCallback(async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.warn('[Fullscreen] exitFullscreen failed:', err);
    }
  }, []);

  const toggle = useCallback(async () => {
    if (document.fullscreenElement) {
      await exit();
    } else {
      await enter();
    }
  }, [enter, exit]);

  // Globalny skrót Cmd+Shift+F (macOS) / Ctrl+Shift+F (Windows/Linux).
  // Activation z keyboard event wystarcza dla Browser Fullscreen API.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const modifier = e.metaKey || e.ctrlKey;
      if (modifier && e.shiftKey && e.code === 'KeyF') {
        e.preventDefault();
        void toggle();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [toggle]);

  return (
    <FullscreenContext.Provider value={{ isFullscreen, toggle, enter, exit }}>
      {children}
    </FullscreenContext.Provider>
  );
}

export function useFullscreen(): FullscreenContextValue {
  const ctx = useContext(FullscreenContext);
  if (!ctx) {
    // Nie wybuchaj jeśli ktoś użyje hooka poza Providerem (np. login page) -
    // zwróć no-op żeby komponenty nie musiały sprawdzać czy są w Layout.
    return {
      isFullscreen: false,
      toggle: async () => {},
      enter: async () => {},
      exit: async () => {},
    };
  }
  return ctx;
}
